/**
 * listHandovers — RPC contract for the read-only listHandovers callable: for
 * the tickets this access link carries, who holds each one now.
 *
 * The capability is the token, not a session — the same possession spine
 * handTicketsOver.action.ts's own header sets out, and `accessToken` is named
 * for the same reason there: an order's token and a slice's token take the
 * identical path, so a recipient reads the handovers they themselves made
 * exactly as the buyer reads theirs.
 *
 * A callable rather than a listener, because the handovers live in a
 * collection the page's own token cannot query and they change only by the
 * holder's own action: one canonical read on mount, and a second device sees
 * a change on its next load.
 *
 * `url` is composed per call from the deployment's own web origin, never
 * stored — one origin change reaches every already-made link, and no
 * document holds a URL that could disagree with the token it was built from.
 *
 * Output fields justified:
 *   handovers — every live handover of the tickets this token carries; a
 *               ticket appears in at most one, since handing on supersedes.
 */
import { z } from "zod";
import { HandoverSchema } from "./handover.entity.ts";
import { SecretToken } from "./ticket.entity.ts";
import { Id } from "./workspace.entity.ts";

export const ListHandoversInput = z
	.object({
		orgId: Id,
		accessToken: SecretToken.describe(
			"the OrderAccess capability token the caller holds — an order's or a slice's, treated identically; possession alone authorizes reading the handovers of the tickets that token's own document carries",
		),
	})
	.strict();
export type ListHandoversInput = z.infer<typeof ListHandoversInput>;

// One live handover as the holder's own page reads it — a projection of the
// stored document (handover.entity.ts), so `name`, `ticketIds` and `sentVia`
// are picked from it rather than re-declared here where they would be free to
// drift from what is on disk.
//
// Fields justified:
//   handoverId — the record's own document id, which takeTicketsBack names
//                to reclaim it.
//   url        — the link this handover minted, recomposed for this call, so
//                the holder can re-send a link they already made.
//   reclaimable — whether THIS caller may take this handover back. A page
//                keeps every card it ever held, each tagged with whoever
//                holds it now, so a caller sees rows for records that are not
//                below them and that takeTicketsBack would refuse. Without
//                this the page offers a control the server rejects.
// Excluded:
//   the slice token — held on HandoverToken, which the Admin SDK alone
//                 reads; `url` already carries the value, and a second copy
//                 would put on the wire the very credential handing a ticket
//                 on and reclaiming one are authorized by.
//   parentHandoverId / orderId — the chain edges the callables walk
//                 server-side; nothing on the holder's page renders them, and
//                 orderId on a recipient's read would name the buyer's order.
//   createdAt   — nothing on the page dates or orders a handover; the
//                 caption reads the person and the channel.
export const ListedHandoverSchema = HandoverSchema.pick({
	name: true,
	ticketIds: true,
	sentVia: true,
})
	.extend({
		handoverId: z
			.string()
			.min(1)
			.describe("the handover record's own document id"),
		url: z
			.string()
			.url()
			.describe(
				"the access link that opens exactly this handover's tickets, recomposed from the deployment's web origin for this call",
			),
		reclaimable: z
			.boolean()
			.describe(
				"whether this caller may take these tickets back — true when the record sits below the caller's own, always true for the buyer's order token; a row that is false is shown as a holder and offers no control",
			),
	})
	.strict();
export type ListedHandover = z.infer<typeof ListedHandoverSchema>;

export const ListHandoversOutput = z
	.object({
		handovers: z.array(ListedHandoverSchema),
	})
	.strict();
export type ListHandoversOutput = z.infer<typeof ListHandoversOutput>;
